import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GripVertical, Settings, Save, Clock } from "lucide-react";
import ArrowBack from "./arrow-back";
import shortid from "shortid";
import { sharedHaversine, sharedValidarLatLng } from "../utils/shared-functions";
import { actualizarOrdenDetallePedido } from "../api/api-detalle-pedido";
// import { obtenerTodasLasUnidades } from "../api/gestion-dts/gestion";

/**
 * Sidebar component for ruteo page
 * Displays a list of items with drag and drop functionality
 * 
 * @param {Array} items - Array of items to display
 * @param {Function} onItemsChange - Callback when items order changes, receives (items) => void
 * @param {Function} onItemClick - Callback when an item is clicked, receives (item) => void
 */
export default function SidebarRuteo({
    items = [],
    onItemsChange,
    onItemClick,
    onViewRoute,
    onClearRoute,
    setProcessingVisible,
    showToast,
    toastMessage,
    vehiculoSelected,
    showRouteSummaryButton = false,
    onShowRouteSummary,
}) {
    const history = useHistory();
    const { t } = useTranslation();
    const [displayItems, setDisplayItems] = useState([]);
    const [draggedItemIndex, setDraggedItemIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [settingsDropdownOpen, setSettingsDropdownOpen] = useState(false);
    const settingsDropdownRef = useRef(null);
    const settingsButtonRef = useRef(null);


    useEffect(() => {
        obtenerDatos();
        // console.log('vehiculoSelected');
        // console.log(vehiculoSelected);
    }, []);

    // Close settings dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                settingsDropdownRef.current &&
                settingsButtonRef.current &&
                !settingsDropdownRef.current.contains(event.target) &&
                !settingsButtonRef.current.contains(event.target)
            ) {
                setSettingsDropdownOpen(false);
            }
        };

        if (settingsDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [settingsDropdownOpen]);

    const obtenerDatos = async () => {
        // const unidades = await obtenerTodasLasUnidades();
        // setListaVehiculos(unidades);
        // console.log('puntoActual');
        // console.log(puntoActual);
    };

    // Update displayItems when items prop changes
    useEffect(() => {
        // Sort items by orden attribute and update displayItems
        // const sortedItems = [...items].sort((a, b) => {
        //     const ordenA = a.orden !== undefined ? a.orden : items.indexOf(a);
        //     const ordenB = b.orden !== undefined ? b.orden : items.indexOf(b);
        //     return ordenA - ordenB;
        // });
        setDisplayItems(items);
    }, [items]);

    const handleDragStart = (e, index) => {
        setDraggedItemIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(index));

        const row = e.target.closest('.ruteo-sidebar-item-row');
        if (row) {
            row.classList.add('ruteo-drag-source');
            const rect = row.getBoundingClientRect();
            const clone = row.cloneNode(true);
            clone.classList.add('ruteo-drag-clone');
            clone.style.position = 'fixed';
            clone.style.left = '-9999px';
            clone.style.top = '0';
            clone.style.width = `${rect.width}px`;
            clone.style.boxShadow = '0 20px 60px rgba(0,0,0,0.35), 0 0 0 1px rgba(0,0,0,0.08)';
            clone.style.opacity = '1';
            clone.style.transform = 'scale(1.06) rotate(-1deg)';
            clone.style.zIndex = '9999';
            clone.style.pointerEvents = 'none';
            clone.style.background = '#fff';
            clone.style.borderRadius = '0.5rem';
            document.body.appendChild(clone);
            e.dataTransfer.setDragImage(clone, Math.min(rect.width / 2, 100), rect.height / 2);
            requestAnimationFrame(() => { try { document.body.removeChild(clone); } catch (_) { } });
        }
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        const sourceIndex = e.dataTransfer.getData('text/plain');
        const dragIndex = sourceIndex !== '' ? parseInt(sourceIndex, 10) : draggedItemIndex;
        if (dragIndex === undefined || dragIndex === null || isNaN(dragIndex) || dragIndex === dropIndex) {
            setDraggedItemIndex(null);
            setDragOverIndex(null);
            return;
        }

        // Create a new array with reordered items
        const newItems = [...displayItems];
        const [draggedItem] = newItems.splice(dragIndex, 1);
        newItems.splice(dropIndex, 0, draggedItem);

        // Update orden attribute based on new index
        const updatedItems = newItems.map((item, index) => ({
            ...item,
            orden: index,
        }));

        setDisplayItems(updatedItems);
        setDraggedItemIndex(null);
        setDragOverIndex(null);

        // Notify parent component of the change
        if (onItemsChange) {
            onItemsChange(updatedItems);
        }
    };

    const handleDragEnd = (e) => {
        document.querySelectorAll('.ruteo-sidebar-item-row.ruteo-drag-source').forEach((el) => {
            el.classList.remove('ruteo-drag-source');
        });
        setDraggedItemIndex(null);
        setDragOverIndex(null);
    };

    const renderItem = (item, index) => {
        // const itemId = getItemId(item, index);
        const isDragging = draggedItemIndex === index;
        const isDragOver = dragOverIndex === index && draggedItemIndex !== index;

        return (
            <div
                key={shortid.generate()}
                className={`ruteo-sidebar-item-row mb-2 rounded-lg p-3 transition-all duration-200 ${isDragging
                    ? 'border-2 border-dashed border-amber-500 bg-amber-50/90 opacity-70 shadow-inner scale-[0.98]'
                    : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                    } ${isDragOver
                        ? 'border-2 border-solid border-blue-500 bg-blue-100 ring-4 ring-blue-300/80 ring-offset-2 scale-[1.02]'
                        : ''
                    }`}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
            >
                {isDragOver && (
                    <div className="text-center text-sm font-semibold text-blue-600 mb-2 py-1 rounded bg-blue-200/60">
                        {t("label_soltar_aqui") || "Soltar aquí"}
                    </div>
                )}
                {isDragging && (
                    <div className="text-center text-xs font-medium text-amber-600 mb-1 opacity-90">
                        {t("label_mover_a_otra_posicion") || "Mover a otra posición"}
                    </div>
                )}
                <div className="flex items-start gap-3">
                    {/* Drag Handle - only this part is draggable so item content click works */}
                    <div
                        className="mt-1 text-gray-400 hover:text-gray-600 flex-shrink-0 select-none cursor-move"
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnd={handleDragEnd}
                    >
                        <GripVertical className="h-5 w-5" />
                    </div>

                    {/* Item Content - click focuses map on item */}
                    <div
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => {
                            if (onItemClick) onItemClick(item);
                        }}
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <div className="text-xs font-semibold text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                                {index + 1}
                            </div>
                            {/* {item.labelPlaca && (
                                <div className="text-sm font-semibold text-gray-700">
                                    {item.labelPlaca}
                                </div>
                            )} */}
                            {item.cliente && (
                                <div className="text-sm font-semibold text-gray-700">
                                    {item.cliente}
                                </div>
                            )}
                        </div>
                        {/* {item.cliente && (
                            <div className="text-sm text-gray-600 mb-1">
                                {item.cliente}
                            </div>
                        )} */}
                        {item.geocerca && (
                            <div className="text-xs text-gray-500 mt-1">
                                {t("label_geocerca") || "Geocerca"}: {item.geocerca}
                            </div>
                        )}
                        {item.tipoCliente && (
                            <div className="text-xs text-gray-500 mt-1">
                                {item.tipoCliente}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const handleSave = async () => {
        try {
            // console.log('handleSave');
            // console.log(displayItems);
            setProcessingVisible(true);
            const response = await actualizarOrdenDetallePedido({
                listaDetallePedido: displayItems,
            });
            setProcessingVisible(false);
            if (response.error === true) {
                showToast(t("alert_no_se_pudo_procesar"));
            }
            if (response.error === false) {
                showToast(t("alert_orden_detalle_pedido_actualizada"));
                await new Promise(resolve => setTimeout(resolve, 1500));
                history.goBack();
            }
        } catch (err) {
            console.log('handleSave err');
            console.log(err);
        }
    };

    const handleGetBetterRouteByPriority = () => {
        try {
            // 1. Clear the route first
            if (typeof onClearRoute === "function") {
                onClearRoute();
            }
            // 2. Sort by priority
            const items = [...displayItems];
            console.log('items');
            console.log(items);
            setProcessingVisible(true);
            items.sort((a, b) => (a.prioridad ?? 0) - (b.prioridad ?? 0));
            console.log('items ordenados');
            console.log(items);
            setDisplayItems(items);
            if (typeof onItemsChange === "function") {
                onItemsChange(items);
            }
            setProcessingVisible(false);
            // 3. Draw the route again with new order
            if (typeof onViewRoute === "function") {
                onViewRoute();
            }
        } catch (err) {
            console.log('handleGetBetterRouteByPriority err');
            console.log(err);
            setProcessingVisible(false);
        }
    };

    const handleViewRoute = () => {
        setSettingsDropdownOpen(false);
        if (typeof onViewRoute === "function") {
            onViewRoute();
        }
    };

    /**
     * handleGetBetterRoute – Optimizes delivery order by distance (nearest-neighbor).
     *
     * Algorithm:
     * 1. Use vehiculoSelected as the starting point (current position).
     * 2. From the current point, always pick the delivery that is closest.
     * 3. Move the "current point" to that delivery and remove it from the list.
     * 4. Repeat until all deliveries are in the new order.
     *
     * To change behavior later you can:
     * - Use a different starting point (e.g. first item, or a depot).
     * - Use another distance metric (e.g. drive time) instead of sharedHaversine.
     * - Change the "closest" rule (e.g. prefer priority + distance).
     */
    const handleGetBetterRoute = () => {
        try {
            // ─── Step 1: Validate vehicle (reference point) ─────────────────────
            if (!vehiculoSelected) {
                showToast(t("alert_vehiculo_no_seleccionado"));
                return;
            }
            const latVehiculo = parseFloat(vehiculoSelected.LATITUD ?? vehiculoSelected.latitud);
            const lngVehiculo = parseFloat(vehiculoSelected.LONGITUD ?? vehiculoSelected.longitud);
            if (Number.isNaN(latVehiculo) || Number.isNaN(lngVehiculo) || !sharedValidarLatLng(latVehiculo, lngVehiculo)) {
                showToast(t("alert_punto_vehiculo_no_valido"));
                return;
            }

            // ─── Step 2: Clear the current route on the map ─────────────────────
            if (typeof onClearRoute === "function") {
                onClearRoute();
            }

            // ─── Step 3: Build listaPedidos and assign distance from vehicle ─────
            // Each delivery gets a "distancia" from vehiculoSelected (for reference;
            // the actual ordering is done in the nearest-neighbor loop below).
            let listaPedidos = [...items];
            for (let i = 0; i < listaPedidos.length; i++) {
                const pedido = listaPedidos[i];
                const lat = pedido.latitud;
                const lng = pedido.longitud;
                if (sharedValidarLatLng(lat, lng) === false) {
                    showToast(t("alert_punto_interes_no_valido") + "\n" + (pedido.cliente || ""));
                    return;
                }
                const latitud = parseFloat(lat);
                const longitud = parseFloat(lng);
                const distancia = sharedHaversine(latitud, longitud, latVehiculo, lngVehiculo);
                if (distancia == null) {
                    showToast((t("alert_error_al_calcular_distancia")) + "\n" + (pedido.cliente || ""));
                    return;
                }
                listaPedidos[i] = { ...pedido, distancia };
            }

            // ─── Step 4: Nearest-neighbor – build optimized order ────────────────
            // Start at vehicle position. Each iteration: pick closest remaining
            // delivery, add it to the route, then move "current point" to that delivery.
            const listaPedidosOptimizada = [];
            let currentLat = latVehiculo;
            let currentLng = lngVehiculo;
            let remaining = [...listaPedidos];

            while (remaining.length > 0) {
                // 4a. Find the index of the delivery closest to (currentLat, currentLng)
                let minDist = Infinity;
                let closestIndex = 0;
                for (let j = 0; j < remaining.length; j++) {
                    const pedido = remaining[j];
                    const dist = sharedHaversine(currentLat, currentLng, parseFloat(pedido.latitud), parseFloat(pedido.longitud));
                    if (dist != null && dist < minDist) {
                        minDist = dist;
                        closestIndex = j;
                    }
                }
                // 4b. Add that delivery to the optimized list with orden = position in route
                const chosen = remaining[closestIndex];
                listaPedidosOptimizada.push({ ...chosen, orden: listaPedidosOptimizada.length });
                // 4c. Move current point to the chosen delivery for the next iteration
                currentLat = parseFloat(chosen.latitud);
                currentLng = parseFloat(chosen.longitud);
                // 4d. Remove chosen from remaining so we don't pick it again
                remaining = remaining.filter((_, idx) => idx !== closestIndex);
            }

            // ─── Step 5: Apply new order and redraw route ────────────────────────
            setDisplayItems(listaPedidosOptimizada);
            if (typeof onItemsChange === "function") {
                onItemsChange(listaPedidosOptimizada);
            }
            if (typeof onViewRoute === "function") {
                onViewRoute();
            }
        } catch (err) {
            console.log('handleGetBetterRoute err');
            console.log(err);
        }
    };

    return (
        <div className="h-full flex flex-col bg-white border-r border-gray-200 w-80 flex-shrink-0">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 relative">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => {
                            history.goBack();
                        }}
                        className="p-1 hover:bg-gray-100 rounded-lg transition-colors relative"
                    >
                        <ArrowBack className="h-4 w-4 text-gray-600 transition-transform hover:scale-110" />
                    </button>
                    <label className="flex-1 text-sm font-medium text-gray-700">
                        {t("label_ruteo")}:{vehiculoSelected?.ID_ACTIVO}
                    </label>
                    <button
                        ref={settingsButtonRef}
                        onClick={() => setSettingsDropdownOpen(!settingsDropdownOpen)}
                        className="p-1 hover:bg-gray-100 rounded-lg transition-colors relative"
                        title={t("label_configuracion") || "Configuración"}
                    >
                        <Settings className="h-4 w-4 text-gray-600 transition-transform hover:scale-110" />
                    </button>
                </div>

                {/* Settings Dropdown Menu */}
                {settingsDropdownOpen && (
                    <div
                        ref={settingsDropdownRef}
                        className="absolute right-4 top-12 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
                    >
                        <div className="py-1">
                            <button
                                key="ver_ruta"
                                onClick={handleViewRoute}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors break-words whitespace-normal"
                            >
                                {t("label_ver_ruta")}
                            </button>
                            <button
                                key="obtener_mejor_ruta"
                                onClick={() => {
                                    handleGetBetterRoute();
                                    setSettingsDropdownOpen(false);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors break-words whitespace-normal"
                            >
                                {t("label_obtener_mejor_ruta")}
                            </button>
                            <button
                                key="obtener_mejor_ruta_por_prioridad"
                                onClick={() => {
                                    handleGetBetterRouteByPriority();
                                    setSettingsDropdownOpen(false);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors break-words whitespace-normal"
                            >
                                {t("label_obtener_mejor_ruta_por_prioridad")}
                            </button>
                        </div>
                    </div>
                )}
            </div>
            <div className="p-4 border-b border-gray-200 flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={() => {
                        handleSave();
                    }}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
                >
                    <Save className="h-4 w-4" />
                    {t("label_guardar")}
                </button>
                {showRouteSummaryButton && onShowRouteSummary && (
                    <button
                        type="button"
                        onClick={onShowRouteSummary}
                        className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                    >
                        <Clock className="h-4 w-4" />
                        {t("label_ver_resumen_recorrido")}
                    </button>
                )}
            </div>

            {/* Items List */}
            <div className="flex-1 overflow-y-auto p-2">
                {displayItems.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        {t("label_no_hay_items") || "No hay items"}
                    </div>
                ) : (
                    <div>
                        {displayItems.map((item, index) => renderItem(item, index))}
                    </div>
                )}
            </div>
        </div>
    );
}

